"use client"

import * as React from "react"
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs"
import { cva, type VariantProps } from "class-variance-authority"
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { cn } from "@/lib/utils"

function Tabs({
  className,
  orientation = "horizontal",
  ...props
}: TabsPrimitive.Root.Props) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      className={cn(
        "group/tabs flex gap-2 data-horizontal:flex-col",
        className
      )}
      {...props}
    />
  )
}

const tabsListVariants = cva(
  "group/tabs-list inline-flex w-fit items-center justify-center rounded-lg p-[3px] text-muted-foreground group-data-horizontal/tabs:h-8 group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col data-[variant=line]:rounded-none",
  {
    variants: {
      variant: {
        default: "bg-muted",
        line: "gap-1 bg-transparent",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function TabsList({
  className,
  variant = "default",
  scrollable = false,
  children,
  ...props
}: TabsPrimitive.List.Props &
  VariantProps<typeof tabsListVariants> & {

    scrollable?: boolean
  }) {
  const list = (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(
        tabsListVariants({ variant }),

        variant === "line" && "relative",
        className
      )}
      {...props}
    >
      {children}
      {variant === "line" ? <TabsIndicator /> : null}
    </TabsPrimitive.List>
  )

  if (!scrollable) {
    return list
  }

  return <ScrollableTabsListShell>{list}</ScrollableTabsListShell>
}

function TabsIndicator({ className, ...props }: TabsPrimitive.Indicator.Props) {
  return (
    <TabsPrimitive.Indicator
      data-slot="tabs-indicator"
      renderBeforeHydration
      className={cn(

        "pointer-events-none absolute bg-primary transition-[translate,width,height] duration-300 ease-out will-change-[translate]",
        "group-data-horizontal/tabs:bottom-0 group-data-horizontal/tabs:left-0 group-data-horizontal/tabs:h-0.5 group-data-horizontal/tabs:w-[var(--active-tab-width)] group-data-horizontal/tabs:translate-x-[var(--active-tab-left)]",
        "group-data-vertical/tabs:right-0 group-data-vertical/tabs:top-0 group-data-vertical/tabs:h-[var(--active-tab-height)] group-data-vertical/tabs:w-0.5 group-data-vertical/tabs:translate-y-[var(--active-tab-top)]",
        className
      )}
      {...props}
    />
  )
}

function ScrollArrow({
  side,
  visible,
  onClick,
}: {
  side: "left" | "right"
  visible: boolean
  onClick: () => void
}) {
  const t = useTranslations("site.ui")
  const Icon = side === "left" ? ChevronLeftIcon : ChevronRightIcon

  return (
    <button
      type="button"
      aria-label={side === "left" ? t("scrollLeft") : t("scrollRight")}
      tabIndex={-1}
      onClick={onClick}
      className={cn(
        "z-10 size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
        visible ? "flex" : "hidden"
      )}
    >
      <Icon className="size-4" />
    </button>
  )
}

function ScrollableTabsListShell({ children }: { children: React.ReactNode }) {
  const viewportRef = React.useRef<HTMLDivElement>(null)
  const [canLeft, setCanLeft] = React.useState(false)
  const [canRight, setCanRight] = React.useState(false)

  const update = React.useCallback(() => {
    const el = viewportRef.current
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    setCanLeft(scrollLeft > 1)
    setCanRight(scrollLeft + clientWidth < scrollWidth - 1)
  }, [])

  React.useEffect(() => {
    const el = viewportRef.current
    if (!el) return

    update()
    el.addEventListener("scroll", update, { passive: true })

    const observer = new ResizeObserver(update)
    observer.observe(el)
    for (const child of Array.from(el.children)) {
      observer.observe(child)
    }

    return () => {
      el.removeEventListener("scroll", update)
      observer.disconnect()
    }
  }, [update])

  const scrollByStep = (direction: -1 | 1) => {
    const el = viewportRef.current
    if (!el) return
    el.scrollBy({
      left: direction * Math.max(el.clientWidth * 0.8, 120),
      behavior: "smooth",
    })
  }

  return (
    <div className="relative flex min-w-0 items-center gap-1">
      <ScrollArrow
        side="left"
        visible={canLeft}
        onClick={() => scrollByStep(-1)}
      />
      <div
        ref={viewportRef}
        className="min-w-0 flex-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&_[data-slot=tabs-trigger]]:flex-none"
      >
        {children}
      </div>
      <ScrollArrow
        side="right"
        visible={canRight}
        onClick={() => scrollByStep(1)}
      />
    </div>
  )
}

function TabsTrigger({
  className,
  activateOnHover = false,
  closable = false,
  onClose,
  onMouseEnter,
  children,
  ...props
}: TabsPrimitive.Tab.Props & {

  activateOnHover?: boolean

  closable?: boolean
  onClose?: () => void
}) {
  const t = useTranslations("site.ui")

  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      onMouseEnter={(event) => {
        onMouseEnter?.(event)
        if (activateOnHover && !event.defaultPrevented) {
          event.currentTarget.click()
        }
      }}
      className={cn(
        "relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-1.5 py-0.5 text-sm font-medium whitespace-nowrap text-foreground/60 transition-all group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 has-data-[icon=inline-end]:pr-1 has-data-[icon=inline-start]:pl-1 aria-disabled:pointer-events-none aria-disabled:opacity-50 dark:text-muted-foreground dark:hover:text-foreground group-data-[variant=default]/tabs-list:data-active:shadow-sm group-data-[variant=line]/tabs-list:data-active:shadow-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-active:bg-transparent dark:group-data-[variant=line]/tabs-list:data-active:border-transparent dark:group-data-[variant=line]/tabs-list:data-active:bg-transparent",
        "data-active:bg-background data-active:text-foreground dark:data-active:border-input dark:data-active:bg-input/30 dark:data-active:text-foreground",
        className
      )}
      {...props}
    >
      {children}
      {closable ? (
        <span
          role="button"
          tabIndex={-1}
          aria-label={t("closeTab")}
          className="-mr-0.5 ml-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation()
            event.preventDefault()
            onClose?.()
          }}
        >
          <XIcon className="size-3" />
        </span>
      ) : null}
    </TabsPrimitive.Tab>
  )
}

function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      className={cn("text-sm outline-none", className)}
      {...props}
    />
  )
}

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  TabsIndicator,
  tabsListVariants,
}
